import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { AnyPlayerStatsIcon } from '@sorare/marketplace/src/components/player/AnyPlayerStatsIcon';
import { PlayerDrawer } from '@sorare/marketplace/src/components/player/PlayerDrawer';

import { PulledCardStars } from '#src/components/essence/CardPullExperience/CardsReveal/PulledCardStars/index.tsx';

import type {
  AnimatedCardFooter_anyCardInterface,
  AnimatedCardFooter_cardPulledCard,
} from './__generated__/index.graphql.ts';

const Footer = styled.div`
  height: var(--quadruple-unit);
  max-width: 120px;
  margin: 0 auto;
`;

const animation = keyframes`
  0% {
    transform: scale(0.75) translateY(calc(var(--triple-unit) * -1));
    opacity: 0;
    z-index: -1;
  }
  99% {
    transform: scale(0.75) translateY(0);
    opacity: 1;
    z-index: -1;
  }
  100% {
    transform: scale(0.75) translateY(0);
    opacity: 1;
    z-index: 0;
  }
`;

const Wrapper = styled(Horizontal).attrs({
  center: true,
})`
  position: relative;
  height: 100%;
  transform: scale(0.75) translateY(calc(var(--triple-unit) * -1));
  opacity: 0;
  transition:
    transform 0.5s ease-in-out,
    opacity 0.5s ease-in-out;

  &.showFooter {
    animation: ${animation} 0.5s ease-in-out forwards;
  }
`;

type Props = {
  pulledCard: AnimatedCardFooter_cardPulledCard;
  ready: boolean;
};
export const AnimatedCardFooter = ({ pulledCard, ready }: Props) => {
  const { revealedCard, quality, gameplayTier } = pulledCard;
  const [showPlayerDetails, setShowPlayerDetails] = useState(false);

  const [showFooter, setShowFooter] = useState(false);

  useEffect(() => {
    if (ready) {
      const to = setTimeout(() => {
        setShowFooter(true);
      }, 1250);
      return () => clearTimeout(to);
    }
    return () => {};
  }, [ready]);

  return (
    <>
      <Footer>
        <Wrapper className={classNames({ showFooter })}>
          {!!revealedCard && !!showFooter && (
            <CardFooter
              left={
                <AnyPlayerStatsIcon
                  backgroundColor="transparent"
                  player={revealedCard.anyPlayer}
                />
              }
              right={
                <PulledCardStars
                  quality={quality}
                  gameplayTier={gameplayTier}
                />
              }
              topGap={0.5}
              onClick={() => {
                setShowPlayerDetails(true);
              }}
            >
              <PlayerScore
                player={revealedCard.anyPlayer}
                mode={AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE}
                animated={false}
                hideTooltip
              />
            </CardFooter>
          )}
        </Wrapper>
      </Footer>
      <PlayerDrawer
        cardOpened={
          showPlayerDetails && revealedCard
            ? {
                playerSlug: revealedCard?.anyPlayer?.slug,
                cardSlug: revealedCard?.slug,
              }
            : null
        }
        onClose={() => {
          setShowPlayerDetails(false);
        }}
      />
    </>
  );
};

AnimatedCardFooter.fragments = {
  anyCardInterface: gql`
    fragment AnimatedCardFooter_anyCardInterface on AnyCardInterface {
      slug
      anyPlayer {
        slug
        ...AnyPlayerStatsIcon_anyPlayer
        ...PlayerScore_anyPlayer
      }
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<AnimatedCardFooter_anyCardInterface>,
  cardPulledCard: gql`
    fragment AnimatedCardFooter_cardPulledCard on CardPullCard {
      id
      quality
      gameplayTier
      revealedCard {
        slug
        anyPlayer {
          slug
          ...AnyPlayerStatsIcon_anyPlayer
          ...PlayerScore_anyPlayer
        }
      }
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<AnimatedCardFooter_cardPulledCard>,
};
